/**
 * The props that make a tile, card or row open the item context menu:
 * right-click (at the pointer), Shift+F10 or the ContextMenu key (at the
 * item), and a 500ms touch long-press that a drift of more than a few pixels
 * cancels (that is a scroll). Spread them on the item's focusable element;
 * a view that composes its own `onKeyDown` runs this one first and skips its
 * own when this one called `preventDefault`.
 *
 * `itemContextMenuProps` is the hook-free form for render callbacks (a grid
 * row renderer cannot call a hook per row).
 */

import { useMemo } from 'react';
import type { KeyboardEvent, MouseEvent, PointerEvent } from 'react';
import type { WorkspaceItem } from '@bower/shared';

import type { ItemSurface } from '../../../stores/selectionStore';
import { openItemMenu, type ItemMenuUiSurface } from './itemMenuStore';

export const LONG_PRESS_MS = 500;
/** How far a touch may drift, in px, and still count as a press. */
const LONG_PRESS_SLOP_PX = 10;

export interface ItemContextMenuProps {
  onContextMenu: (event: MouseEvent<HTMLElement>) => void;
  onKeyDown: (event: KeyboardEvent<HTMLElement>) => void;
  onPointerDown: (event: PointerEvent<HTMLElement>) => void;
  onPointerMove: (event: PointerEvent<HTMLElement>) => void;
  onPointerUp: () => void;
  onPointerCancel: () => void;
}

/** One finger, one press: a second touch anywhere replaces the first. */
let press: { timer: ReturnType<typeof setTimeout>; x: number; y: number } | null = null;

function cancelPress() {
  if (press) clearTimeout(press.timer);
  press = null;
}

/** Below-left of the item, for the keyboard openers. */
function itemCorner(el: HTMLElement) {
  const rect = el.getBoundingClientRect();
  return { x: rect.left, y: rect.bottom };
}

export function itemContextMenuProps(
  item: WorkspaceItem,
  surface: ItemSurface = 'canvas',
  uiSurface: ItemMenuUiSurface = surface === 'tree' ? 'tree' : 'row',
): ItemContextMenuProps {
  return {
    onContextMenu: (event) => {
      event.preventDefault();
      cancelPress();
      const el = event.currentTarget;
      // A keyboard-raised contextmenu carries no pointer position.
      const fromKeyboard = event.clientX === 0 && event.clientY === 0;
      openItemMenu(
        item,
        fromKeyboard ? itemCorner(el) : { x: event.clientX, y: event.clientY },
        el,
        surface,
        { trigger: fromKeyboard ? 'keyboard' : 'pointer', uiSurface },
      );
    },
    onKeyDown: (event) => {
      if (event.target !== event.currentTarget) return;
      if (event.key !== 'ContextMenu' && !(event.key === 'F10' && event.shiftKey)) return;
      event.preventDefault();
      openItemMenu(item, itemCorner(event.currentTarget), event.currentTarget, surface, {
        trigger: 'keyboard',
        uiSurface,
      });
    },
    onPointerDown: (event) => {
      cancelPress();
      if (event.pointerType !== 'touch') return;
      const el = event.currentTarget;
      const at = { x: event.clientX, y: event.clientY };
      press = {
        ...at,
        timer: setTimeout(() => {
          press = null;
          openItemMenu(item, at, el, surface, { trigger: 'longpress', uiSurface });
        }, LONG_PRESS_MS),
      };
    },
    onPointerMove: (event) => {
      if (!press) return;
      const moved = Math.hypot(event.clientX - press.x, event.clientY - press.y);
      if (moved > LONG_PRESS_SLOP_PX) cancelPress();
    },
    onPointerUp: cancelPress,
    onPointerCancel: cancelPress,
  };
}

export function useItemContextMenu(
  item: WorkspaceItem,
  uiSurface: ItemMenuUiSurface = 'row',
): ItemContextMenuProps {
  return useMemo(() => itemContextMenuProps(item, 'canvas', uiSurface), [item, uiSurface]);
}
